<script setup lang="ts">
  import { computed, ref, onMounted, watch } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import ComboField from '@/components/admin/form/ComboField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import AudioPlaybackButton from '@/components/app/calls/audioPlaybackButton.vue'
  import LegalFormDuplicateWarning from '@/components/app/cases/LegalFormDuplicateWarning.vue'
  import {
    TitleEnum,
    CompanyLegalFormEnum,
    BlankEnum,
    EntityTypeEnum,
    type CustomerCaseData,
    type CustomerCaseDataRequest,
    type EntityRequest,
    type Call
  } from '@/types/generated'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore.ts'
  import { CasesService, CallsService } from '@/types/generated'
  import type { PatchedCustomerCaseRequest } from '@/types/generated'
  import { Building2, Landmark, User } from 'lucide-vue-next'
  import { useToast } from '@/components/ui/toast'
  import * as yup from 'yup'

  type EntityRelationship = 'client' | 'opponent'
  const props = defineProps<{
    fieldPrefix: EntityRelationship
  }>()

  const { t } = useTranslation({
    en: {
      caseUpdated: 'Case Updated',
      city: 'City',
      companyName: 'Company Name',
      contactPersonFirstName: 'Contact Person First Name',
      contactPersonLastName: 'Contact Person Last Name',
      contactPersonTitle: 'Contact Person Title',
      diverse: 'Diverse',
      email: 'Email',
      entityType: 'Entity Type',
      error: 'Error',
      errorUpdatingCase: 'There was an error updating the case. Please try again.',
      firstName: 'First Name',
      frau: 'Mrs',
      herr: 'Mr',
      houseNumber: 'House Nr.',
      institution: 'Institution',
      institutionName: 'Institution Name',
      lastName: 'Last Name',
      legalForm: 'Legal Form',
      legalPerson: 'Legal Person',
      naturalPerson: 'Natural Person',
      phone: 'Phone',
      postalCode: 'Postal Code',
      street: 'Street',
      title: 'Title',
      noSpecification: 'Not specified',
      saFrance: 'SA (France)',
      saSpain: 'SA (Spain)',
      soleProprietorship: 'Sole Proprietorship',
      foundation: 'Foundation',
      specificTitle: 'Specific Title',
      contactPersonSpecificTitle: 'Contact Person Specific Title'
    },
    de: {
      caseUpdated: 'Anfrage aktualisiert',
      city: 'Ort',
      companyName: 'Firma',
      contactPersonFirstName: 'Kontaktperson Vorname',
      contactPersonLastName: 'Kontaktperson Nachname',
      contactPersonTitle: 'Kontaktperson Anrede',
      diverse: 'Divers',
      email: 'E-Mail',
      entityType: 'Rechtliche Person',
      error: 'Fehler',
      errorUpdatingCase:
        'Beim Aktualisieren der Anfrage ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
      firstName: 'Vorname',
      frau: 'Frau',
      herr: 'Herr',
      houseNumber: 'Hausnummer',
      institution: 'Institution',
      institutionName: 'Institutionsname',
      lastName: 'Nachname',
      legalForm: 'Rechtsform',
      legalPerson: 'Juristische Person',
      naturalPerson: 'Natürliche Person',
      noSpecification: 'Keine Angabe',
      phone: 'Telefon',
      postalCode: 'PLZ',
      saFrance: 'SA (Frankreich)',
      saSpain: 'SA (Spanien)',
      soleProprietorship: 'Einzelunternehmen',
      foundation: 'Stiftung',
      street: 'Straße',
      title: 'Anrede',
      specificTitle: 'Titel',
      contactPersonSpecificTitle: 'Kontaktperson Titel'
    },
    fr: {
      caseUpdated: 'Dossier mis à jour',
      city: 'Ville',
      companyName: "Nom de l'entreprise",
      contactPersonFirstName: 'Prénom de la personne de contact',
      contactPersonLastName: 'Nom de la personne de contact',
      contactPersonTitle: 'Titre de la personne de contact',
      diverse: 'Autre',
      email: 'E-mail',
      entityType: "Type d'entité",
      error: 'Erreur',
      errorUpdatingCase:
        'Une erreur est survenue lors de la mise à jour du dossier. Veuillez réessayer.',
      firstName: 'Prénom',
      frau: 'Madame',
      herr: 'Monsieur',
      houseNumber: 'Numéro',
      institution: 'Institution',
      institutionName: "Nom de l'institution",
      lastName: 'Nom',
      legalForm: 'Forme juridique',
      legalPerson: 'Personne morale',
      naturalPerson: 'Personne physique',
      noSpecification: 'Non spécifié',
      phone: 'Téléphone',
      postalCode: 'Code postal',
      saFrance: 'SA (France)',
      saSpain: 'SA (Espagne)',
      soleProprietorship: 'Entreprise individuelle',
      foundation: 'Fondation',
      street: 'Rue',
      title: 'Titre',
      specificTitle: 'Titre spécifique',
      contactPersonSpecificTitle: 'Titre spécifique de la personne de contact'
    }
  })

  // Toast
  const { toast } = useToast()

  // Get the case store
  const caseStore = useCaseStore()
  const caseRecord = computed(() => caseStore.caseRecord)

  // Call data for audio playback
  const callData = ref<Call | null>(null)
  const isLoadingCall = ref(false)

  const caseRecordData = computed(() => {
    if (!caseRecord.value?.data) return null
    return caseRecord.value?.data[props.fieldPrefix]
  })

  const entityTypeOptions = computed(() => [
    {
      value: EntityTypeEnum.NATURAL_PERSON,
      title: t('naturalPerson'),
      icon: User
    },
    {
      value: EntityTypeEnum.LEGAL_PERSON,
      title: t('legalPerson'),
      icon: Building2
    },
    {
      value: EntityTypeEnum.INSTITUTION,
      title: t('institution'),
      icon: Landmark
    }
  ])

  // Template refs for AutoSavingForm components
  const detailsFormRef = ref<InstanceType<typeof AutoSavingForm> | null>(null)
  const addressFormRef = ref<InstanceType<typeof AutoSavingForm> | null>(null)

  // Computed properties for entity data with getters and setters
  const entityType = computed({
    get: () => {
      return caseRecordData.value?.entity_type || EntityTypeEnum.NATURAL_PERSON
    },
    set: (value: EntityTypeEnum) => {
      if (!caseRecordData.value) return
      caseRecordData.value.entity_type = value
    }
  })

  const companyName = computed({
    get: () => caseRecordData.value?.company_name || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.company_name = value
    }
  })

  const legalForm = computed({
    get: () => caseRecordData.value?.company_legal_form || '',
    set: (value: CompanyLegalFormEnum | BlankEnum) => {
      if (!caseRecordData.value) return
      caseRecordData.value.company_legal_form = value
    }
  })

  const applySuggestedName = () => {
    if (!companyName.value || !legalForm.value) return

    const pattern = new RegExp(
      `[\\s,\\-]*${legalForm.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`,
      'i'
    )
    const cleanedName = companyName.value.replace(pattern, '').trim()

    if (cleanedName) {
      companyName.value = cleanedName
    }
  }

  const institutionName = computed({
    get: () => caseRecordData.value?.institution_name || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.institution_name = value
    }
  })

  const title = computed({
    get: () => caseRecordData.value?.title || '',
    set: (value: TitleEnum) => {
      if (!caseRecordData.value) return
      caseRecordData.value.title = value
    }
  })

  const specificTitle = computed({
    get: () => caseRecordData.value?.specific_title || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.specific_title = value
    }
  })

  const firstName = computed({
    get: () => caseRecordData.value?.first_name || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.first_name = value
    }
  })

  const lastName = computed({
    get: () => caseRecordData.value?.last_name || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.last_name = value
    }
  })

  const email = computed({
    get: () => caseRecordData.value?.email || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.email = value
    }
  })

  const phone = computed({
    get: () => caseRecordData.value?.phone || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.phone = value
    }
  })

  const street = computed({
    get: () => caseRecordData.value?.street || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.street = value
    }
  })

  const streetNumber = computed({
    get: () => caseRecordData.value?.street_number || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.street_number = value
    }
  })

  const zip = computed({
    get: () => caseRecordData.value?.zip || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.zip = value
    }
  })

  const city = computed({
    get: () => caseRecordData.value?.city || '',
    set: (value: string) => {
      if (!caseRecordData.value) return
      caseRecordData.value.city = value
    }
  })

  // Email validation rule
  const emailValidation = yup
    .string()
    .email('Invalid email format')
    .test('email-validation', 'Invalid email format', (value) => {
      if (!value) return true // Allow empty email (not required)
      return yup.string().email().isValidSync(value)
    })

  // Predefined options for specific title
  const specificTitleOptions = [
    { value: 'Prof.', title: 'Prof.' },
    { value: 'Prof. Dr.', title: 'Prof. Dr.' },
    { value: 'Dr.', title: 'Dr.' },
    { value: 'Dr. Dr.', title: 'Dr. Dr.' }
  ]

  // Computed properties to determine entity type
  const isLegalPerson = computed(() => entityType.value === 'legal_person')
  const isInstitution = computed(() => entityType.value === 'institution')

  // Computed properties for labels based on entity type
  const titleLabel = computed(() => (isLegalPerson.value ? t('contactPersonTitle') : t('title')))
  const specificTitleLabel = computed(() =>
    isLegalPerson.value ? t('contactPersonSpecificTitle') : t('specificTitle')
  )
  const firstNameLabel = computed(() =>
    isLegalPerson.value ? t('contactPersonFirstName') : t('firstName')
  )
  const lastNameLabel = computed(() =>
    isLegalPerson.value ? t('contactPersonLastName') : t('lastName')
  )

  // Fetch call data when source_call_uuid is available
  const fetchCallData = async (callUuid: string) => {
    try {
      isLoadingCall.value = true
      const fetchedCall = await CallsService.callsRetrieve({ uuid: callUuid })
      callData.value = fetchedCall as Call
    } catch (error) {
      console.error('Error fetching call data:', error)
      callData.value = null
    } finally {
      isLoadingCall.value = false
    }
  }

  // Watch for changes in source_call_uuid and fetch call data
  watch(
    () => caseRecord.value?.source_call_uuid,
    (newCallUuid) => {
      if (newCallUuid) {
        fetchCallData(newCallUuid)
      } else {
        callData.value = null
      }
    },
    { immediate: true }
  )

  // Computed properties for recording availability
  const canShowFirstNameRecording = computed(() => {
    return (
      callData.value?.audio_recording_url &&
      callData.value?.phone_number?.can_record_calls &&
      callData.value?.result?.first_name_recording_start_seconds != null &&
      callData.value?.result?.first_name_recording_end_seconds != null &&
      props.fieldPrefix === 'client' // Only show for client entity
    )
  })

  const canShowLastNameRecording = computed(() => {
    return (
      callData.value?.audio_recording_url &&
      callData.value?.phone_number?.can_record_calls &&
      callData.value?.result?.last_name_recording_start_seconds != null &&
      callData.value?.result?.last_name_recording_end_seconds != null &&
      props.fieldPrefix === 'client' // Only show for client entity
    )
  })

  const canShowEmailRecording = computed(() => {
    return (
      callData.value?.audio_recording_url &&
      callData.value?.phone_number?.can_record_calls &&
      callData.value?.result?.email_recording_start_seconds != null &&
      callData.value?.result?.email_recording_end_seconds != null &&
      props.fieldPrefix === 'client' // Only show for client entity
    )
  })

  const canShowPhoneRecording = computed(() => {
    return (
      callData.value?.audio_recording_url &&
      callData.value?.phone_number?.can_record_calls &&
      callData.value?.result?.phone_number_recording_start_seconds != null &&
      callData.value?.result?.phone_number_recording_end_seconds != null &&
      props.fieldPrefix === 'client' // Only show for client entity
    )
  })

  // Internal function to update the case
  const updateCase = async (updates: Partial<PatchedCustomerCaseRequest>) => {
    try {
      if (caseRecord.value) {
        const uuid = caseRecord.value.uuid
        if (!uuid) {
          throw new Error('Case UUID is missing')
        }

        // Update the case without immediately refreshing
        await CasesService.casesPartialUpdate({ uuid, requestBody: updates })

        toast({
          title: t('caseUpdated'),
          variant: 'default'
        })
      }
    } catch (error) {
      console.error('Error updating case:', error)
      toast({
        title: t('error'),
        description: t('errorUpdatingCase'),
        variant: 'destructive'
      })
    }
  }

  // Event handlers with internal saving
  const handleDetailsFormSave = (values: Record<string, any>) => {
    // Skip saving if email is invalid
    if (
      values[`${props.fieldPrefix}_email`] &&
      !emailValidation.isValidSync(values[`${props.fieldPrefix}_email`])
    ) {
      return
    }

    const updates: Partial<EntityRequest> = {}
    const entityKey = props.fieldPrefix // 'client' or 'opponent'

    // Map form field values to API field names
    if (values[`${props.fieldPrefix}_title`] !== undefined) {
      updates.title = values[`${props.fieldPrefix}_title`]
    }

    if (values[`${props.fieldPrefix}_specific_title`] !== undefined) {
      updates.specific_title = values[`${props.fieldPrefix}_specific_title`]
    }

    if (values[`${props.fieldPrefix}_first_name`] !== undefined) {
      updates.first_name = values[`${props.fieldPrefix}_first_name`]
    }

    if (values[`${props.fieldPrefix}_last_name`] !== undefined) {
      updates.last_name = values[`${props.fieldPrefix}_last_name`]
    }

    if (values[`${props.fieldPrefix}_email`] !== undefined) {
      updates.email = values[`${props.fieldPrefix}_email`]
    }

    if (values[`${props.fieldPrefix}_phone`] !== undefined) {
      updates.phone = values[`${props.fieldPrefix}_phone`]
    }

    if (values[`${props.fieldPrefix}_company_name`] !== undefined) {
      updates.company_name = values[`${props.fieldPrefix}_company_name`]
    }

    if (values[`${props.fieldPrefix}_legal_form`] !== undefined) {
      updates.company_legal_form = values[`${props.fieldPrefix}_legal_form`]
    }

    if (values[`${props.fieldPrefix}_institution_name`] !== undefined) {
      updates.institution_name = values[`${props.fieldPrefix}_institution_name`]
    }

    if (values[`${props.fieldPrefix}_entity_type`] !== undefined) {
      updates.entity_type = values[`${props.fieldPrefix}_entity_type`]
    }

    // Only update if we have changes
    if (Object.keys(updates).length > 0) {
      // Make sure entity_type is included if it's not in the updates
      if (!updates.entity_type && caseRecordData.value) {
        updates.entity_type = caseRecordData.value.entity_type
      }

      const payload: Partial<PatchedCustomerCaseRequest> = {
        data: {} as CustomerCaseDataRequest
      }

      if (!payload.data) return

      // We need to merge with existing data to ensure all required fields are present
      if (caseRecord.value?.data?.[entityKey]) {
        // Create a merged entity with existing data and our updates
        payload.data[entityKey] = {
          ...updates
        } as EntityRequest
      } else {
        // If there's no existing data, we need to make sure our updates include all required fields
        payload.data[entityKey] = updates as EntityRequest
      }

      updateCase(payload)
    }
  }

  const handleAddressFormSave = (values: Record<string, any>) => {
    const updates: Partial<EntityRequest> = {}
    const entityKey = props.fieldPrefix // 'client' or 'opponent'

    if (values[`${props.fieldPrefix}_street`] !== undefined) {
      updates.street = values[`${props.fieldPrefix}_street`]
    }

    if (values[`${props.fieldPrefix}_street_number`] !== undefined) {
      updates.street_number = values[`${props.fieldPrefix}_street_number`]
    }

    if (values[`${props.fieldPrefix}_zip`] !== undefined) {
      updates.zip = values[`${props.fieldPrefix}_zip`]
    }

    if (values[`${props.fieldPrefix}_city`] !== undefined) {
      updates.city = values[`${props.fieldPrefix}_city`]
    }

    // Only update if we have changes
    if (Object.keys(updates).length > 0) {
      const payload: Partial<PatchedCustomerCaseRequest> = {
        data: {} as CustomerCaseDataRequest
      }

      if (!payload.data) return

      // We need to merge with existing data to ensure all required fields are present
      if (caseRecord.value?.data?.[entityKey]) {
        // Create a merged entity with existing data and our updates
        payload.data[entityKey] = {
          ...caseRecord.value.data[entityKey],
          ...updates
        } as EntityRequest
      } else {
        // If there's no existing data, we need to make sure our updates include all required fields
        payload.data[entityKey] = updates as EntityRequest
      }

      updateCase(payload)
    }
  }
</script>

<template>
  <div>
    <!-- Details Form -->
    <AutoSavingForm
      ref="detailsFormRef"
      :debounceDelay="1500"
      :throttleInterval="2000"
      :preventInitialSave="false"
      @save="handleDetailsFormSave"
    >
      <!-- Entity Type Selection -->
      <div class="mb-3">
        <SelectField
          :name="`${fieldPrefix}_entity_type`"
          :label="t('entityType')"
          :model-value="entityType"
          :options="entityTypeOptions"
          @update:model-value="entityType = $event"
        />
      </div>

      <!-- Legal person specific fields -->
      <div v-if="isLegalPerson">
        <div class="mb-3">
          <InputField
            :name="`${fieldPrefix}_company_name`"
            :data-cy="`input-${fieldPrefix}_company_name`"
            :label="t('companyName')"
            :model-value="companyName"
            @update:model-value="companyName = $event"
          />
          <LegalFormDuplicateWarning
            :company-name="companyName"
            :legal-form="legalForm"
            :on-fix="applySuggestedName"
          />
        </div>
        <div class="mb-3">
          <SelectField
            :name="`${fieldPrefix}_legal_form`"
            :label="t('legalForm')"
            :model-value="legalForm"
            :options="[
              { value: '', title: t('noSpecification') },
              { value: 'GmbH', title: 'GmbH' },
              { value: 'UG (haftungsbeschränkt)', title: 'UG (haftungsbeschränkt)' },
              { value: 'GmbH & Co. KG', title: 'GmbH & Co. KG' },
              { value: 'AG', title: 'AG' },
              { value: 'AG & Co. KG', title: 'AG & Co. KG' },
              { value: 'KG', title: 'KG' },
              { value: 'OHG', title: 'OHG' },
              { value: 'GBR', title: 'GbR' },
              { value: 'PartG', title: 'PartG' },
              { value: 'PartG mbB', title: 'PartG mbB' },
              { value: 'KGaA', title: 'KGaA' },
              { value: 'eG', title: 'eG' },
              { value: 'eV', title: 'e.V.' },
              { value: 'Einzelunternehmen', title: t('soleProprietorship') },
              { value: 'Stiftung', title: t('foundation') },
              { value: 'WEG', title: 'WEG' },
              { value: 'SE', title: 'SE' },
              { value: 'EWIV', title: 'EWIV' },
              { value: 'Limited', title: 'Limited' },
              { value: 'PLC', title: 'PLC' },
              { value: 'SARL', title: 'SARL' },
              { value: 'SAS', title: 'SAS' },
              { value: 'SA (FR)', title: t('saFrance') },
              { value: 'SCN', title: 'SCN' },
              { value: 'S.A.R.L.', title: 'S.A.R.L.' },
              { value: 'SRL', title: 'SRL' },
              { value: 'SPA', title: 'SPA' },
              { value: 'SL', title: 'SL' },
              { value: 'SA (ES)', title: t('saSpain') },
              { value: 'BV', title: 'BV' },
              { value: 'NV', title: 'NV' },
              { value: 'AB', title: 'AB' },
              { value: 'Oy', title: 'Oy' },
              { value: 'AS', title: 'AS' },
              { value: 's.r.o.', title: 's.r.o.' },
              { value: 'Kft.', title: 'Kft.' },
              { value: 'Sp. z o.o.', title: 'Sp. z o.o.' },
              { value: 'OOO', title: 'OOO' }
            ]"
            @update:model-value="legalForm = $event"
          />
        </div>
      </div>

      <!-- Institution specific fields -->
      <div v-if="isInstitution">
        <div class="mb-3">
          <InputField
            :name="`${fieldPrefix}_institution_name`"
            :data-cy="`input-${fieldPrefix}_institution_name`"
            :label="t('institutionName')"
            :model-value="institutionName"
            @update:model-value="institutionName = $event"
          />
        </div>
      </div>

      <!-- Fields that depend on entity type for label but always show -->
      <div
        v-if="!isInstitution"
        class="mb-3"
      >
        <SelectField
          :name="`${fieldPrefix}_title`"
          :label="titleLabel"
          :model-value="title"
          :options="[
            { value: 'Herr', title: t('herr') },
            { value: 'Frau', title: t('frau') },
            { value: 'Divers', title: t('diverse') }
          ]"
          @update:model-value="title = $event"
        />
      </div>
      <div
        v-if="!isInstitution"
        class="mb-3"
      >
        <ComboField
          :name="`${fieldPrefix}_specific_title`"
          :label="specificTitleLabel"
          :model-value="specificTitle"
          :options="specificTitleOptions"
          @update:model-value="specificTitle = $event"
        />
      </div>
      <div
        v-if="!isInstitution"
        class="mb-3"
      >
        <div class="flex items-end gap-2">
          <div class="min-w-0 flex-1">
            <InputField
              :name="`${fieldPrefix}_first_name`"
              :data-cy="`input-${fieldPrefix}_first_name`"
              :label="firstNameLabel"
              :model-value="firstName"
              @update:model-value="firstName = $event"
            />
          </div>
          <AudioPlaybackButton
            v-if="canShowFirstNameRecording"
            :audioUrl="callData?.audio_recording_url || ''"
            :startTime="callData?.result?.first_name_recording_start_seconds?.toString() || ''"
            :endTime="callData?.result?.first_name_recording_end_seconds?.toString() || ''"
          />
        </div>
      </div>
      <div
        v-if="!isInstitution"
        class="mb-3"
      >
        <div class="flex items-end gap-2">
          <div class="min-w-0 flex-1">
            <InputField
              :name="`${fieldPrefix}_last_name`"
              :data-cy="`input-${fieldPrefix}_last_name`"
              :label="lastNameLabel"
              :model-value="lastName"
              @update:model-value="lastName = $event"
            />
          </div>
          <AudioPlaybackButton
            v-if="canShowLastNameRecording"
            :audioUrl="callData?.audio_recording_url || ''"
            :startTime="callData?.result?.last_name_recording_start_seconds?.toString() || ''"
            :endTime="callData?.result?.last_name_recording_end_seconds?.toString() || ''"
          />
        </div>
      </div>

      <!-- Common fields for all entity types -->
      <div class="mb-3">
        <div class="flex items-end gap-2">
          <div class="min-w-0 flex-1">
            <InputField
              type="email"
              :name="`${fieldPrefix}_email`"
              :data-cy="`input-${fieldPrefix}_email`"
              :label="t('email')"
              :model-value="email"
              :rules="emailValidation"
              @update:model-value="email = $event"
            />
          </div>
          <AudioPlaybackButton
            v-if="canShowEmailRecording"
            :audioUrl="callData?.audio_recording_url || ''"
            :startTime="callData?.result?.email_recording_start_seconds?.toString() || ''"
            :endTime="callData?.result?.email_recording_end_seconds?.toString() || ''"
          />
        </div>
      </div>
      <div class="mb-3">
        <div class="flex items-end gap-2">
          <div class="min-w-0 flex-1">
            <InputField
              :name="`${fieldPrefix}_phone`"
              :data-cy="`input-${fieldPrefix}_phone`"
              :label="t('phone')"
              :model-value="phone"
              @update:model-value="phone = $event"
            />
          </div>
          <AudioPlaybackButton
            v-if="canShowPhoneRecording"
            :audioUrl="callData?.audio_recording_url || ''"
            :startTime="callData?.result?.phone_number_recording_start_seconds?.toString() || ''"
            :endTime="callData?.result?.phone_number_recording_end_seconds?.toString() || ''"
          />
        </div>
      </div>
    </AutoSavingForm>

    <AutoSavingForm
      ref="addressFormRef"
      :debounceDelay="1500"
      :throttleInterval="2000"
      :preventInitialSave="false"
      @save="handleAddressFormSave"
    >
      <div class="mb-3">
        <div
          class="d-flex"
          style="gap: 0.25rem"
        >
          <div style="flex: 3">
            <InputField
              :name="`${fieldPrefix}_street`"
              :data-cy="`input-${fieldPrefix}_street`"
              :label="t('street')"
              :model-value="street"
              @update:model-value="street = $event"
            />
          </div>
          <div style="flex: 1">
            <InputField
              :name="`${fieldPrefix}_street_number`"
              :data-cy="`input-${fieldPrefix}_street_number`"
              :label="t('houseNumber')"
              :model-value="streetNumber"
              @update:model-value="streetNumber = $event"
            />
          </div>
        </div>
      </div>
      <div class="mb-3">
        <div
          class="d-flex"
          style="gap: 0.25rem"
        >
          <div style="flex: 1">
            <InputField
              :name="`${fieldPrefix}_zip`"
              :data-cy="`input-${fieldPrefix}_zip`"
              :label="t('postalCode')"
              :model-value="zip"
              @update:model-value="zip = $event"
            />
          </div>
          <div style="flex: 3">
            <InputField
              :name="`${fieldPrefix}_city`"
              :data-cy="`input-${fieldPrefix}_city`"
              :label="t('city')"
              :model-value="city"
              @update:model-value="city = $event"
            />
          </div>
        </div>
      </div>
    </AutoSavingForm>
  </div>
</template>
